// client/src/components/unidades/sections/UnitsSection.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useUserUnidadesByUser, useRelationshipsMutations } from '../../../hooks/useRelationships';
import { useUnidadesPorConta } from '../../../hooks/useUnidades';
import { useRoles } from '../../../hooks/useRoles';
import { 
    Save, Loader2, MapPin, XCircle, Search, ChevronDown, RefreshCw, AlertCircle, CheckCircle, ChevronUp, DollarSign, Settings, Plus
} from 'lucide-react';
import useAlert from '../../../hooks/useAlert';
import { useTenantI18n } from '../../../hooks/useAccounts';
import { getCurrencySymbol } from '../../../utils/formatters';
import { useQueryClient } from '@tanstack/react-query';
import DesvincularUnidadeModal from '../tabs/DesvincularUnidadeModal';

const getSafeId = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const UnitsSection = ({ user, contextData }) => {
    const { setAlert } = useAlert();
    // ✅ ACHADO: rótulos abaixo eram fixos em "(R$)", sem ler
    // configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const currencySymbol = getCurrencySymbol(currency, locale);
    const queryClient = useQueryClient();
    
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUnidades, setSelectedUnidades] = useState([]);
    const [comissoesConfig, setComissoesConfig] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [expandedUnidade, setExpandedUnidade] = useState(null);
    const [roleUnidadeId, setRoleUnidadeId] = useState(null);
    
    const [dadosCarregados, setDadosCarregados] = useState(false);
    const initialSyncDone = useRef(false);

    const [vinculoParaDesvincular, setVinculoParaDesvincular] = useState(null);
    const [showDesvincularModal, setShowDesvincularModal] = useState(false);

    const targetUserId = getSafeId(user);
    const targetAccountId = getSafeId(contextData?.contaId);
    const isProfissional = !!user?.profissionalId || !!user?.isProfissional;

    const { data: rolesData, isLoading: loadingRoles } = useRoles({
        account: targetAccountId,
        enabled: !!targetAccountId
    });

    const { data: unidadesData, isLoading: isLoadingUnidades, refetch: refetchUnidades } = useUnidadesPorConta(targetAccountId, {
        enabled: !!targetAccountId
    });

    const { data: vinculosData, isLoading: isLoadingVinculos, refetch: refetchVinculos } = useUserUnidadesByUser(targetUserId, targetAccountId, {
        enabled: !!targetUserId && !!targetAccountId
    });

    const { createUserUnidade, updateUserUnidade, deleteUserUnidade, invalidateRelationships } = useRelationshipsMutations();

    useEffect(() => {
        if (rolesData) {
            let roles = Array.isArray(rolesData) ? rolesData : (rolesData.data || []);
            const roleUnidade = roles.find(r => r.name?.toLowerCase().includes('profissional') || r.nome?.toLowerCase().includes('profissional'));
            if (roleUnidade) setRoleUnidadeId(getSafeId(roleUnidade));
        }
    }, [rolesData]);

    const vinculosAtivosDoBanco = useMemo(() => {
        if (!vinculosData?.data) return [];
        return vinculosData.data.filter(v => v.status === 'ativo');
    }, [vinculosData]);

    // ✅ Sincronização segura sem loop
    useEffect(() => {
        if (initialSyncDone.current || isLoadingVinculos) return;
        if (!vinculosData) return;

        if (vinculosAtivosDoBanco.length > 0) {
            const ids = vinculosAtivosDoBanco.map(v => getSafeId(v.unidade)).filter(Boolean);
            const comissoes = {};
            vinculosAtivosDoBanco.forEach(v => {
                const unidId = getSafeId(v.unidade);
                if (unidId) comissoes[unidId] = v.comissao || { ativo: false, faixas: [{ type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }], tipoAplicacao: 'sobre_servico', considerarTaxas: false, considerarDescontos: true };
            });
            setSelectedUnidades(ids);
            setComissoesConfig(comissoes);
        } else {
            setSelectedUnidades([]);
            setComissoesConfig({});
        }
        
        initialSyncDone.current = true;
        setDadosCarregados(true);
    }, [vinculosAtivosDoBanco, vinculosData, isLoadingVinculos]);

    const unidadesBase = useMemo(() => unidadesData?.data || [], [unidadesData]);

    const vinculosMap = useMemo(() => {
        const map = new Map();
        vinculosAtivosDoBanco.forEach(v => {
            const unidId = getSafeId(v.unidade);
            if (unidId) map.set(unidId, v);
        });
        return map;
    }, [vinculosAtivosDoBanco]);

    const unidadesRenderizaveis = useMemo(() => {
        if (!dadosCarregados) return [];

        let arrayFinal = unidadesBase.map(unidade => {
            const unidId = getSafeId(unidade);
            const isSelected = selectedUnidades.includes(unidId);
            const vinculoNoBanco = vinculosMap.get(unidId);
            
            let comissaoData = comissoesConfig[unidId] || vinculoNoBanco?.comissao || {
                ativo: false, tipoAplicacao: 'sobre_servico', faixas: [{ type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }]
            };

            return {
                ...unidade,
                _id: unidId,
                isSelected,
                vinculoNoBanco,
                hasVinculoNoBanco: !!vinculoNoBanco,
                comissao: comissaoData
            };
        });

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            arrayFinal = arrayFinal.filter(u => (u.nome && u.nome.toLowerCase().includes(term)) || (u.localidade && u.localidade.toLowerCase().includes(term)));
        }
        
        return arrayFinal;
    }, [unidadesBase, selectedUnidades, comissoesConfig, vinculosMap, searchTerm, dadosCarregados]);

    const handleToggleUnidade = (unidadeId) => {
        if (!isEditing) return;

        const idStr = getSafeId(unidadeId);
        const isSelected = selectedUnidades.includes(idStr);
        const vinculoNoBanco = vinculosMap.get(idStr);

        if (isSelected) {
            if (vinculoNoBanco) {
                setVinculoParaDesvincular(vinculoNoBanco);
                setShowDesvincularModal(true);
            } else {
                const novos = selectedUnidades.filter(id => id !== idStr);
                setSelectedUnidades(novos);
            }
        } else {
            const novos = [...selectedUnidades, idStr];
            setSelectedUnidades(novos);
            
            setComissoesConfig(prev => {
                if (!prev[idStr]) {
                    return { ...prev, [idStr]: { ativo: false, tipoAplicacao: 'sobre_servico', faixas: [{ type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }] } };
                }
                return prev;
            });
            setExpandedUnidade(idStr);
        }
    };

    const handleComissaoChange = (unidadeId, field, value) => {
        if (!isEditing) return;
        setComissoesConfig(prev => {
            const configAtual = prev[unidadeId] || { ativo: false, faixas: [], tipoAplicacao: 'sobre_servico' };
            return { ...prev, [unidadeId]: { ...configAtual, [field]: value } };
        });
    };

    const handleFaixaChange = (unidadeId, index, field, value) => {
        if (!isEditing) return;
        setComissoesConfig(prev => {
            const configAtual = prev[unidadeId] || { ativo: false, faixas: [] };
            const faixas = [...(configAtual.faixas || [])];
            
            if (!faixas[index]) {
                faixas[index] = { type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } };
            }

            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                const parentObj = faixas[index][parent] || {};
                faixas[index] = { ...faixas[index], [parent]: { ...parentObj, [child]: value } };
            } else {
                faixas[index] = { ...faixas[index], [field]: value };
            }

            return { ...prev, [unidadeId]: { ...configAtual, faixas } };
        });
    };

    const handleAddFaixa = (unidadeId) => {
        if (!isEditing) return;
        setComissoesConfig(prev => {
            const configAtual = prev[unidadeId] || { ativo: false, faixas: [] };
            const faixas = [...(configAtual.faixas || []), { type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }];
            return { ...prev, [unidadeId]: { ...configAtual, faixas } };
        });
    };

    const handleRemoveFaixa = (unidadeId, index) => {
        if (!isEditing) return;
        setComissoesConfig(prev => {
            const configAtual = prev[unidadeId];
            if (!configAtual || !configAtual.faixas) return prev;
            const faixas = configAtual.faixas.filter((_, idx) => idx !== index);
            return { ...prev, [unidadeId]: { ...configAtual, faixas } };
        });
    };

    const handleDesvincularSuccess = () => {
        invalidateRelationships();
        refetchVinculos();
        queryClient.invalidateQueries({ queryKey: ['user-unidades'] });
        
        if (vinculoParaDesvincular) {
            const idStr = getSafeId(vinculoParaDesvincular.unidade);
            setSelectedUnidades(prev => prev.filter(id => id !== idStr));
        }
        
        setShowDesvincularModal(false);
        setVinculoParaDesvincular(null);
    };

    const handleReload = () => {
        refetchUnidades();
        refetchVinculos();
        initialSyncDone.current = false;
        setDadosCarregados(false);
    };

    const handleSave = async () => {
        if (!targetUserId || !targetAccountId) return setAlert('Dados insuficientes para salvar', 'error');
        if (!roleUnidadeId) return setAlert('Role unidade não encontrada. Contate o suporte.', 'error');

        setIsSaving(true);
        try {
            const errors = [];

            // 1. Salvar ou atualizar
            for (const unidId of selectedUnidades) {
                try {
                    const vinculoExistente = vinculosMap.get(unidId);
                    const comissao = comissoesConfig[unidId] || { ativo: false, faixas: [] };

                    if (vinculoExistente) {
                        await updateUserUnidade.mutateAsync({
                            id: getSafeId(vinculoExistente),
                            data: { comissao, status: 'ativo', role: roleUnidadeId }
                        });
                    } else {
                        await createUserUnidade.mutateAsync({
                            user: targetUserId, unidade: unidId, account: targetAccountId,
                            role: roleUnidadeId, status: 'ativo', comissao
                        });
                    }
                } catch (error) {
                    errors.push({ unidId, error: error.message });
                }
            }

            // 2. Remover os desmarcados no UI mas que estão no banco
            const idsDesmarcados = vinculosAtivosDoBanco
                .map(v => getSafeId(v.unidade))
                .filter(unidId => !selectedUnidades.includes(unidId));

            for (const unidId of idsDesmarcados) {
                const vinculo = vinculosMap.get(unidId);
                if (vinculo) {
                    try {
                        await deleteUserUnidade.mutateAsync(getSafeId(vinculo));
                    } catch (error) {
                        errors.push({ unidId, error: error.message });
                    }
                }
            }

            invalidateRelationships();
            queryClient.invalidateQueries({ queryKey: ['user-unidades'] }); 
            await refetchVinculos();
            
            if (errors.length === 0) setAlert('Unidades atualizadas com sucesso!', 'success');
            else setAlert(`Atualização concluída com erros.`, 'warning');
            
            setIsEditing(false);
            setExpandedUnidade(null);
            initialSyncDone.current = false;
            setDadosCarregados(false);

        } catch (error) {
            console.error('❌ Erro ao salvar unidades:', error);
            setAlert('Erro ao salvar configurações', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isProfissional) {
        return (
            <div className="form-section text-center py-12 border border-dashed border-gray-200 mt-4 rounded-xl bg-gray-50">
                <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Apenas Profissionais</h3>
                <p className="text-gray-500 mb-4">Habilite a opção "É Profissional" na aba Perfil.</p>
            </div>
        );
    }

    if (isLoadingUnidades || isLoadingVinculos || !dadosCarregados) {
        return <div className="flex justify-center items-center py-12"><Loader2 size={32} className="animate-spin text-primary" /></div>;
    }

    const unidadesVinculadas = unidadesRenderizaveis.filter(u => u.isSelected);
    const unidadesNaoVinculadas = unidadesRenderizaveis.filter(u => !u.isSelected);

    // ✅ RENDERIZADOR PADRÃO DO TOGGLE AMARELO
    const renderUnidadeRow = (unidade) => {
        const isExpanded = expandedUnidade === unidade._id;
        
        return (
            <div key={unidade._id} className="border-b border-gray-100 last:border-b-0 bg-white">
                <div className={`flex items-center justify-between p-4 transition-colors ${!isEditing ? 'opacity-70' : 'hover:bg-gray-50'}`}>
                    <div className="flex items-center gap-4 flex-1">
                        <button 
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleToggleUnidade(unidade._id); }}
                            disabled={!isEditing}
                            className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors focus:outline-none flex-shrink-0 ${unidade.isSelected ? 'bg-amber-400' : 'bg-gray-200'}`}
                        >
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${unidade.isSelected ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                        </button>
                        
                        <div className="flex-1 min-w-0">
                            <span className="text-[15px] font-medium text-gray-800 block truncate">{unidade.nome}</span>
                            {unidade.localidade && <p className="text-xs text-gray-500 mt-0.5 block truncate">{unidade.localidade}/{unidade.uf}</p>}
                        </div>
                    </div>
                    
                    <div 
                        className={`flex items-center gap-3 ${unidade.isSelected && isEditing ? 'cursor-pointer group' : ''}`}
                        onClick={() => { if(isEditing && unidade.isSelected) setExpandedUnidade(isExpanded ? null : unidade._id) }}
                    >
                        <span className="text-xs text-gray-400 hidden sm:block">
                            {unidade.comissao?.ativo ? 'Comissão definida' : 'Comissão não definida'}
                        </span>
                        {unidade.isSelected && (
                            <ChevronDown size={18} className={`text-gray-400 transition-transform group-hover:text-gray-600 ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                    </div>
                </div>

                {isExpanded && unidade.isSelected && (
                    <div className="p-5 bg-gray-50/50 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-4">
                            <label className="checkbox-label text-sm flex items-center cursor-pointer">
                                <div className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors mr-3 ${unidade.comissao.ativo ? 'bg-amber-400' : 'bg-gray-300'}`}>
                                    <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${unidade.comissao.ativo ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                                </div>
                                <input 
                                    type="checkbox" 
                                    checked={unidade.comissao.ativo || false} 
                                    onChange={(e) => handleComissaoChange(unidade._id, 'ativo', e.target.checked)} 
                                    className="hidden"
                                    disabled={!isEditing}
                                />
                                <span className="font-medium text-gray-800">Ativar Comissão Personalizada nesta Unidade</span>
                            </label>
                        </div>

                        {unidade.comissao.ativo && (
                            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm max-w-2xl">
                                <div className="mb-4">
                                    <label className="text-xs font-medium text-gray-600 block mb-1">Incidir Sobre</label>
                                    <select 
                                        value={unidade.comissao.tipoAplicacao || 'sobre_servico'} 
                                        onChange={(e) => handleComissaoChange(unidade._id, 'tipoAplicacao', e.target.value)} 
                                        disabled={!isEditing}
                                        className="form-input text-sm w-full md:w-1/2 bg-white"
                                    >
                                        <option value="sobre_servico">Valor do Serviço</option>
                                        <option value="sobre_lucro">Lucro Líquido</option>
                                        <option value="sobre_valor_total">Valor Total da Venda</option>
                                    </select>
                                </div>
                                
                                <div className="space-y-3">
                                    <label className="text-xs font-medium text-gray-600 block mb-2">Faixas de Comissão</label>
                                    {unidade.comissao.faixas?.map((faixa, idx) => (
                                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                            <div className="grid grid-cols-12 gap-3 items-center mb-3">
                                                <div className="col-span-12 md:col-span-4">
                                                    <label className="text-[11px] text-gray-500 uppercase tracking-wider">Tipo</label>
                                                    <select
                                                        value={faixa.tipo || faixa.type || 'percentual'}
                                                        onChange={(e) => handleFaixaChange(unidade._id, idx, 'tipo', e.target.value)}
                                                        disabled={!isEditing}
                                                        className="form-input text-sm w-full mt-1 bg-white"
                                                    >
                                                        <option value="percentual">Porcentagem (%)</option>
                                                        <option value="valor_fixo">Valor Fixo ({currencySymbol})</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-12 md:col-span-8">
                                                    <label className="text-[11px] text-gray-500 uppercase tracking-wider">Valor</label>
                                                    <input
                                                        type="number"
                                                        value={faixa.valor || 0}
                                                        onChange={(e) => handleFaixaChange(unidade._id, idx, 'valor', parseFloat(e.target.value) || 0)}
                                                        disabled={!isEditing}
                                                        className="form-input text-sm w-full mt-1"
                                                        min="0"
                                                        step={(faixa.tipo || faixa.type) === 'percentual' ? '0.1' : '0.01'}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="grid grid-cols-12 gap-3 items-center">
                                                <div className="col-span-12 md:col-span-4">
                                                    <label className="text-[11px] text-gray-500 uppercase tracking-wider">Limite</label>
                                                    <select
                                                        value={faixa.limite?.tipo || faixa.limite?.type || 'sem_limite'}
                                                        onChange={(e) => handleFaixaChange(unidade._id, idx, 'limite.tipo', e.target.value)}
                                                        disabled={!isEditing}
                                                        className="form-input text-sm w-full mt-1 bg-white"
                                                    >
                                                        <option value="sem_limite">Sem Limite</option>
                                                        <option value="valor_maximo">Máx ({currencySymbol})</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-12 md:col-span-8">
                                                    {(faixa.limite?.tipo === 'valor_maximo' || faixa.limite?.type === 'valor_maximo') && (
                                                        <>
                                                            <label className="text-[11px] text-gray-500 uppercase tracking-wider">Valor Máximo ({currencySymbol})</label>
                                                            <input
                                                                type="number"
                                                                value={faixa.limite?.valor || 0}
                                                                onChange={(e) => handleFaixaChange(unidade._id, idx, 'limite.valor', parseFloat(e.target.value) || 0)}
                                                                disabled={!isEditing}
                                                                className="form-input text-sm w-full mt-1"
                                                                min="0"
                                                                step="0.01"
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {unidade.comissao.faixas.length > 1 && isEditing && (
                                                <div className="mt-3 flex justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFaixa(unidade._id, idx)}
                                                        className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded bg-red-50"
                                                    >
                                                        Remover faixa
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    
                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={() => handleAddFaixa(unidade._id)}
                                            className="text-xs text-primary hover:text-primary-dark font-medium flex items-center mt-2 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                        >
                                            <Plus size={14} className="mr-1"/> Adicionar Faixa Extra
                                        </button>
                                    )}
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mt-5 pt-4 border-t border-gray-100">
                                    <label className="checkbox-label text-sm">
                                        <input
                                            type="checkbox"
                                            checked={unidade.comissao.considerarTaxas || false}
                                            onChange={(e) => handleComissaoChange(unidade._id, 'considerarTaxas', e.target.checked)}
                                            disabled={!isEditing}
                                            className="form-checkbox"
                                        />
                                        <span className="checkbox-custom"></span>
                                        <span className="checkbox-label-text text-gray-700">Descontar Taxas do Cartão</span>
                                    </label>
                                    <label className="checkbox-label text-sm">
                                        <input
                                            type="checkbox"
                                            checked={unidade.comissao.considerarDescontos !== false}
                                            onChange={(e) => handleComissaoChange(unidade._id, 'considerarDescontos', e.target.checked)}
                                            disabled={!isEditing}
                                            className="form-checkbox"
                                        />
                                        <span className="checkbox-custom"></span>
                                        <span className="checkbox-label-text text-gray-700">Descontar Promoções</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="form-content">
            <div className="form-header flex items-start justify-between mb-2">
                <div className="flex-1">
                    <h3 className="form-title flex items-center mb-1 text-primary text-lg">
                        <MapPin size={22} className="mr-2"/>
                        Unidades de Atendimento
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Unidades onde {user?.name || 'o profissional'} atende</p>
                    
                    {!isEditing && !isLoadingUnidades && (
                        <div className="text-xs text-green-600 mt-2 flex items-center bg-green-50 px-2 py-1 rounded-md font-medium w-fit">
                            <CheckCircle size={14} className="mr-1" />
                            {unidadesVinculadas.length} vínculo(s) ativo(s)
                        </div>
                    )}
                </div>

                {!isEditing ? (
                    <button onClick={() => setIsEditing(true)} className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700" disabled={!roleUnidadeId}>
                        <MapPin size={16} className="icon text-gray-500" /> Editar Unidades
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button onClick={() => { setIsEditing(false); handleReload(); }} className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700">
                            <XCircle size={16} className="icon text-gray-500" /> Cancelar
                        </button>
                        <button onClick={handleSave} disabled={isSaving} className="action-button action-button-primary bg-primary text-white shadow-sm">
                            {isSaving ? <Loader2 size={16} className="icon animate-spin" /> : <Save size={16} className="icon" />}
                            Salvar Vínculos
                        </button>
                    </div>
                )}
            </div>

            <div className="form-tabs-content p-0 mt-4 border-t border-gray-100 pt-6">
                {isEditing && (
                    <div className="mb-6 relative px-2 flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Buscar unidades por nome ou localidade..."
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm shadow-sm"
                            />
                        </div>
                        {/* Antes era um botao Tailwind avulso (largura fixa 130px,
                            cor propria) em vez de action-button-secondary */}
                        <button
                            type="button"
                            onClick={handleReload}
                            className="action-button action-button-secondary"
                        >
                            <RefreshCw size={16} className="icon" /> Atualizar
                        </button>
                    </div>
                )}

                {unidadesRenderizaveis.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200 mt-4 mx-2">
                        {searchTerm ? 'Nenhuma unidade encontrada na busca.' : 'Nenhuma unidade disponível.'}
                    </div>
                ) : (
                    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        <div className="divide-y divide-gray-100">
                            {unidadesRenderizaveis.filter(u => u.isSelected).map(renderUnidadeRow)}
                            {isEditing && unidadesRenderizaveis.filter(u => !u.isSelected).map(renderUnidadeRow)}
                        </div>
                    </div>
                )}
            </div>

            {showDesvincularModal && vinculoParaDesvincular && (
                <DesvincularUnidadeModal
                    show={showDesvincularModal}
                    onClose={() => {
                        setShowDesvincularModal(false);
                        setVinculoParaDesvincular(null);
                        if (vinculoParaDesvincular?.unidade) {
                            const idStr = getSafeId(vinculoParaDesvincular.unidade);
                            if (!selectedUnidades.includes(idStr)) setSelectedUnidades([...selectedUnidades, idStr]);
                        }
                    }}
                    vinculo={vinculoParaDesvincular}
                    userNome={user?.name || ''}
                    unidadeNome={vinculoParaDesvincular.unidade?.nome || ''}
                    onSuccess={handleDesvincularSuccess}
                />
            )}
        </div>
    );
};

export default UnitsSection;